import React, { useEffect, useState } from "react";
import ModalComponent from "../../../components/modals";
import FormTextField from "../../../components/textField/formTextField";
import LaddaButton from "../../../components/buttons/laddaButton";
import SimpleSelect from "../../../components/selects/simpleSelect";
import AddFeature from "../modals/addFeature";
import VerificationModal from "../../../components/modals/verificationModal";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Grid } from "@mui/material";
import {
  callActionApiAsync,
  postSaveBiomedicalEquipmentModelFeature
} from "../../../services/apiService";
import ToastNotification from "../../../components/toastNotification";
import { icons } from "../../../constants";

const validationSchema = yup.object({
  technicalFeatures: yup
    .number()
    .typeError("La caracteristica es obligatoria")
    .required("La caracteristica es obligatoria"),
  technicalFeaturesDescription: yup
    .string()
    .required("La descripcion es obligatoria")
});

const ModelFeatureModal = ({
  open,
  close,
  selectedRow,
  modelId,
  userId,
  featureOptions,
  setFeatureOptions,
  affectedEquipmentCount,
  onSaved
}) => {
  const [showToast, setShowToast] = useState(false);
  const [toastConfig, setToastConfig] = useState({
    severity: "warn",
    summary: "",
    detail: ""
  });
  const [openAddFeatureModal, setOpenAddFeatureModal] = useState(false);
  const [openVerification, setOpenVerification] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      technicalFeatures: "",
      technicalFeaturesDescription: ""
    }
  });

  useEffect(() => {
    if (!open) return;
    if (selectedRow) {
      setValue("technicalFeatures", selectedRow.featureId);
      setValue("technicalFeaturesDescription", selectedRow.description);
    } else {
      reset({
        technicalFeatures: "",
        technicalFeaturesDescription: ""
      });
    }
  }, [open, reset, selectedRow, setValue]);

  const handleClose = () => {
    reset({
      technicalFeatures: "",
      technicalFeaturesDescription: ""
    });
    setPendingFormData(null);
    setOpenVerification(false);
    close();
  };

  const submitFeature = async (formData) => {
    await callActionApiAsync(
      () =>
        postSaveBiomedicalEquipmentModelFeature({
          id: selectedRow?.id || 0,
          modelId,
          userId,
          featureId: formData.technicalFeatures,
          description: formData.technicalFeaturesDescription
        }),
      (data) => {
        onSaved?.(data);
        handleClose();
      },
      (error) => {
        setToastConfig({
          severity: "error",
          summary: "Caracteristicas tecnicas",
          detail:
            typeof error === "string" && error
              ? error
              : "No se pudo guardar la caracteristica tecnica del modelo."
        });
        setShowToast(true);
      }
    );
  };

  const onSubmit = (formData) => {
    setPendingFormData(formData);
    setOpenVerification(true);
  };

  const handleConfirmSave = async () => {
    if (!pendingFormData) return;
    await submitFeature(pendingFormData);
  };

  return (
    <>
      <ModalComponent
        open={open}
        handleClose={handleClose}
        title={
          selectedRow
            ? "Editar caracteristica tecnica del modelo"
            : "Agregar caracteristica tecnica al modelo"
        }
        actions={<div className="HContainer j-center w-complete mt-10"></div>}
        maxWidth="760px"
      >
        <ToastNotification
          showToast={showToast}
          setShowToast={setShowToast}
          severity={toastConfig.severity}
          summary={toastConfig.summary}
          detail={toastConfig.detail}
        />

        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid item xs={10} md={5}>
              <p className="create-label">Caracteristica:</p>
              <SimpleSelect
                name="technicalFeatures"
                control={control}
                options={featureOptions}
                hideLabel
                error={errors.technicalFeatures}
                margin={"0"}
              />
            </Grid>
            <Grid item xs={2} md={1} className="VContainer align-end">
              <LaddaButton
                width={"40px"}
                fontSize={"12px"}
                height={"40px"}
                variant="contained"
                margin={"0"}
                onClick={() => setOpenAddFeatureModal(true)}
              >
                <img className="btn-icon" src={icons.plusIcon} alt="icon" />
              </LaddaButton>
            </Grid>
            <Grid item xs={12} md={6}>
              <p className="create-label">Descripcion:</p>
              <FormTextField
                name="technicalFeaturesDescription"
                control={control}
                label=""
                error={errors.technicalFeaturesDescription}
                margin={"0"}
              />
            </Grid>
            <Grid item xs={12} className="HContainer j-center mt-15">
              <LaddaButton type="submit" variant="contained">
                {selectedRow ? "Guardar cambios" : "Agregar caracteristica"}
              </LaddaButton>
            </Grid>
          </Grid>
        </form>

        <AddFeature
          open={openAddFeatureModal}
          close={() => setOpenAddFeatureModal(false)}
          setFeatureOptions={setFeatureOptions}
          featureOptions={featureOptions}
        />
      </ModalComponent>

      <VerificationModal
        open={openVerification}
        handleClose={() => setOpenVerification(false)}
        alertText={
          affectedEquipmentCount > 0
            ? `Este cambio sobrescribira las caracteristicas tecnicas de ${affectedEquipmentCount} equipo(s) asociado(s) a este modelo y tambien quedara como base para los nuevos equipos. Desea continuar?`
            : "Esta plantilla tecnica quedara como base para los nuevos equipos de este modelo. Actualmente no hay equipos asociados para sobrescribir. Desea continuar?"
        }
        onConfirm={handleConfirmSave}
        onCancel={() => setOpenVerification(false)}
        iconSeverity="warning"
        yesNo
      />
    </>
  );
};

export default ModelFeatureModal;
